import { Component, Renderer2, HostListener, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { log } from 'console';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactUsService } from '../services/contact-us.service';

declare var grecaptcha: any;
const RECAPTCHA_SITE_KEY = '6LeqndQqAAAAALaX8eLUq4I-Uk65p_gjFTzUFZL8';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  standalone: false
})
export class FooterComponent {
  form!: FormGroup;

  lang: string = 'en';
  dynamicCopyright: string = '';
  langChangeSubscription!: Subscription;

  isLargeScreen: boolean = false;

  toastState: boolean = false;

  errorState: boolean = false;

  showSuccessModal: boolean = false;

  private isBrowser: boolean;

  constructor(
    public translate: TranslateService,
    private renderer: Renderer2,
    private fb: FormBuilder,
    private contactUsService: ContactUsService,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  @HostListener('window:resize', [])
  onResize(): void {
    this.checkScreenSize(); // Check screen size on window resize
  }

  private checkScreenSize(): void {
    if (typeof window === 'undefined') return; // no window during SSR
    this.isLargeScreen = window.innerWidth > 992;
  }

  showToast() {
    if (this.form.valid) {
      // this.toastState = true;
      setTimeout(() => {
        this.toastState = false;
      }, 3000);
    } else {
      this.showErrorToast();
    }
  }
  showErrorToast() {
    this.form.markAllAsTouched();

    this.errorState = true;
    setTimeout(() => {
      this.errorState = false;
    }, 3000);
  }

  closeToast() {
    this.toastState = false;
    this.errorState = false;
  }

  ngOnInit() {


    // const script = this.renderer.createElement('script');
    // script.src = '//uae.fw-cdn.com/40142888/85641.js';
    // script.setAttribute('chat', 'false');
    // script.async = true; // Optional: load script asynchronously
    // this.renderer.appendChild(document.body, script);

    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      honeypot: [''],
    });

    this.checkScreenSize();

    this.langChangeSubscription = this.translate.onLangChange.subscribe(
      (event: LangChangeEvent) => {
        const newLanguage = event.lang;
        this.lang = newLanguage;
      }
    );
    const currentYear = new Date().getFullYear();
    this.updateDynamicCopyright(currentYear);

    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.updateDynamicCopyright(currentYear);
    });
  }

  handleSubmit(event: Event): void {

    event.preventDefault();
    this.form.markAllAsTouched();

    if (this.form.valid) {
      // Patch value before submission (if needed)
      this.form.patchValue({
        email: this.form.get('email')?.value.trim(), // Trim spaces if necessary
      });

      const zohopayload = {
        contact: {
          Email: this.form.get('email')?.value || "",
          PageIdentification: 'hr-newsletter'
        }
      };

      // reCAPTCHA loads lazily (only when someone actually subscribes) and a
      // fresh token is sent to our server, which verifies it before forwarding.
      this.ensureRecaptcha().then(() => {
        grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' }).then((recaptchaToken: string) => {
          this.contactUsService.submitLead({ ...zohopayload, recaptchaToken, honeypot: this.form.get('honeypot')?.value }).subscribe({
            next: () => { },
            error: (error) => console.error("Error submitting form", error)
          });
        });
      }).catch((err) => console.error("reCAPTCHA load failed", err));

      this.showSuccessModal = true;
      setTimeout(() => {
        this.closeModal();
      }, 2000);

      // Reset form after submission (optional)
      this.form.reset();
    }
  }

  // Load the reCAPTCHA v3 script on demand and resolve once grecaptcha is ready.
  private ensureRecaptcha(): Promise<void> {
    return new Promise((resolve) => {
      if (typeof grecaptcha !== 'undefined' && grecaptcha.execute) { resolve(); return; }
      if (!document.querySelector('script[src*="recaptcha/api.js"]')) {
        const s = document.createElement('script');
        s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        s.async = true;
        s.defer = true;
        document.head.appendChild(s);
      }
      const poll = setInterval(() => {
        if (typeof grecaptcha !== 'undefined' && grecaptcha.execute) {
          clearInterval(poll);
          resolve();
        }
      }, 100);
    });
  }

  closeModal(): void {
    this.showSuccessModal = false;  // Hide the modal
  }

  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (control?.hasError('required')) {
      switch (controlName) {
        case 'email':
          return this.translate.instant('footer.subscribe.errorMessage');
        default:
          return '';
      }
    }
    if (control?.hasError('email')) {
      return 'Email must be formatted correctly.';
    }

    return '';
  }

  updateDynamicCopyright(currentYear: number): void {
    this.translate
      .get('footer.copyright', { year: currentYear })
      .subscribe((translatedText: string) => {
        this.dynamicCopyright = translatedText;
      });
  }

  setLang(data: string) {
    this.lang = data;
    if (data === 'en') {
      this.switchLanguage('en');
      this.renderer.setAttribute(document.body, 'dir', 'ltr');
    } else if (data === 'ar') {
      this.switchLanguage('ar');
      this.renderer.setAttribute(document.body, 'dir', 'rtl');
    }
  }



  switchLanguage(lang: string) {
    this.translate.use(lang);
    // localStorage.setItem('language', lang);
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
  openAppleStore(): void {
    const appStoreUrl = 'https://apps.apple.com/ae/app/infithra/id6503617676';
    window.open(appStoreUrl, '_blank');
  }
  navigateToExternal(event: MouseEvent) {
    event.preventDefault();
    window.open('https://app.infithra.com/auth/login', '_blank');
  }

  openPlayStore(): void {
    const appStoreUrl =
      'https://play.google.com/store/apps/details?id=com.kpi.infithraESS';
    window.open(appStoreUrl, '_blank');
  }
}